var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useState, useMemo, useRef } from 'react';
/**
 * Hook to run an async effect on mount and another on unmount.
 *
 * This is necessary because of intricacies of useEffect and StrictMode
 * @see https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
 */
export var useAsyncEffect = function (mountCallback, unmountCallback, deps) {
    if (deps === void 0) { deps = []; }
    var isMounted = useRef(false);
    var _a = useState(false), isLoading = _a[0], setIsLoading = _a[1];
    var _b = useState(undefined), error = _b[0], setError = _b[1];
    var _c = useState(), result = _c[0], setResult = _c[1];
    useEffect(function () {
        isMounted.current = true;
        return function () {
            isMounted.current = false;
        };
    }, []);
    useEffect(function () {
        var ignore = false;
        var mountSucceeded = false;
        (function () { return __awaiter(void 0, void 0, void 0, function () {
            var result_1, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Promise.resolve()];
                    case 1:
                        _a.sent(); // wait for the initial unmount in Strict mode - avoids double mutation
                        if (!isMounted.current || ignore) {
                            return [2 /*return*/];
                        }
                        setIsLoading(true);
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, mountCallback()];
                    case 3:
                        result_1 = _a.sent();
                        mountSucceeded = true;
                        if (isMounted.current && !ignore) {
                            setError(undefined);
                            setResult(result_1);
                            setIsLoading(false);
                        }
                        else {
                            // Component was unmounted before the mount callback returned, cancel it
                            unmountCallback();
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        error_1 = _a.sent();
                        if (!isMounted.current)
                            return [2 /*return*/];
                        setError(error_1);
                        setIsLoading(false);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        }); })();
        return function () {
            ignore = true;
            if (mountSucceeded) {
                unmountCallback()
                    .then(function () {
                    if (!isMounted.current)
                        return;
                    setResult(undefined);
                })
                    .catch(function (error) {
                    if (!isMounted.current)
                        return;
                    setError(error);
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return useMemo(function () { return ({ result: result, error: error, isLoading: isLoading }); }, [result, error, isLoading]);
};
